import { createSlice } from "@reduxjs/toolkit";
import axios from "../../services/axios";
import { allApi } from "utils/config";
import { toast } from "react-toastify";
import { ERROR_MESSAGE } from "utils/const";
import { captureException } from "@sentry/react";

const initialState = {
  notificationsData: {},
  pollingStatus: false,
  isFetchingMore: false,
  currentFrom: 0,
  pollingEnabled: true,
  networkErrorShown: false,
  refreshApis: [],
  refreshApisVersion: 0,
  isPollingPending: false,
};

const notifications = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setNotificationsData: (state, action) => {
      state.notificationsData = action.payload;
    },
    appendNotificationsData: (state, action) => {
      const existing = state.notificationsData?.data || [];
      state.notificationsData = {
        ...action.payload,
        data: [...existing, ...(action.payload?.data || [])],
      };
    },
    setPollingStatus: (state, action) => {
      state.pollingStatus = action.payload;
    },
    setIsFetchingMore: (state, action) => {
      state.isFetchingMore = action.payload;
    },
    setCurrentFrom: (state, action) => {
      state.currentFrom = action.payload;
    },
    setPollingEnabled: (state, action) => {
      state.pollingEnabled = action.payload;
    },
    setNetworkErrorShown: (state, action) => {
      state.networkErrorShown = action.payload;
    },
    setRefreshApis: (state, action) => {
      state.refreshApis = action.payload;
      state.refreshApisVersion += 1;
    },
    setIsPollingPending: (state, action) => {
      state.isPollingPending = action.payload;
    },
  },
});

export const { setNotificationsData, appendNotificationsData, setPollingStatus, setPollingEnabled, setNetworkErrorShown, setIsFetchingMore, setCurrentFrom, setRefreshApis, setIsPollingPending } =
  notifications.actions;

export const fetchNotifications =
  () =>
  async (dispatch) => {
    try {
      const { data = {} } = await axios.get(allApi.getNotifications, {
        params: { from: 0 },
      });
      if (data) {
        dispatch(setNotificationsData(data));
        dispatch(setCurrentFrom(0));
        dispatch(setPollingEnabled(true));
        dispatch(setNetworkErrorShown(false));
      }
    } catch (err) {
      if (!err.response || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        dispatch(setPollingEnabled(false));
        dispatch(setNetworkErrorShown(true));
      } else {
        toast.error(ERROR_MESSAGE);
      }
      captureException(err);
    }
  };

export const fetchMoreNotifications =
  (from) =>
  async (dispatch) => {
    try {
      dispatch(setIsFetchingMore(true));
      const { data = {} } = await axios.get(allApi.getNotifications, {
        params: { from },
      });
      if (data) {
        dispatch(appendNotificationsData(data));
        dispatch(setCurrentFrom(from));
        dispatch(setPollingEnabled(true));
        dispatch(setNetworkErrorShown(false));
      }
    } catch (err) {
      if (!err.response || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        dispatch(setPollingEnabled(false));
        dispatch(setNetworkErrorShown(true));
      } else {
        toast.error(ERROR_MESSAGE);
      }
      captureException(err);
    } finally {
      dispatch(setIsFetchingMore(false));
    }
  };

export const getNotificationPollingStatus = (userId) => async (dispatch, getState) => {
  const { isPollingPending } = getState().notifications;
  if (isPollingPending) return;

  dispatch(setIsPollingPending(true));
  try {
    const payload = {
      id: userId || "",
    };
    const { data = {} } = await axios.post(allApi.getNotificationPollingStatus, payload);
    if (data) {
      dispatch(setPollingStatus(data?.poll || false));
      dispatch(setPollingEnabled(true));
      dispatch(setNetworkErrorShown(false));
      if (data.refresh_apis?.length > 0) {
        dispatch(setRefreshApis(data.refresh_apis));
      }
    }
  } catch (err) {
    if (!err.response || err.code === "ERR_NETWORK" || err.message === "Network Error") {
      dispatch(setPollingEnabled(false));
      dispatch(setNetworkErrorShown(true));
    } else {
      toast.error(ERROR_MESSAGE);
    }
    captureException(err);
  } finally {
    dispatch(setIsPollingPending(false));
  }
};

export default notifications.reducer;
